import { QueryClient } from '@tanstack/react-query'
import { POSTCustomerPeriodRequest } from '@/feature/financial-model/ecommerce/revenues/api/types'
import { postCustomerSegmentPeriodBulk, CUSTOMER_SEGMENTS_DATA_KEY } from '@/feature/financial-model/ecommerce/revenues/api/customer-segments'
import { parsePeriod, filterRowsStartingFrom } from '@/utils/sidepanel'
import { resetGlobalQueryKey, TOTALS_KEY } from '@/api/query-keys'

const getPeriodMapKey = (year: number, month: number) => `${year}-${String(month).padStart(2, `0`)}`

export const applySegmentValueToRow = async (
  value: number,
  startFrom: { year: number; month: number } | undefined,
  _exactPeriod: { year: number; month: number } | undefined,
  financialModelId: string | undefined,
  payload: any,
  queryClient: QueryClient
) => {
  if (!financialModelId || !payload?.customerSegmentRowKey || !payload.customerSegmentPeriods || !payload.customerSegmentId) return

  const { customerSegmentRowKey, customerSegmentId } = payload
  const rows = payload.customerSegmentPeriods.filter((row: any) => row.period_type === `forecast`)
  const periodValuesByKey = payload?.periodValuesByKey as Record<string, number> | undefined

  if (!rows.length) return

  const rowsToUpdate = periodValuesByKey
    ? rows.filter((row: any) => {
        const { year, month } = parsePeriod(row.period)
        if (year === undefined || month === undefined) return false
        return periodValuesByKey[getPeriodMapKey(year, month)] !== undefined
      })
    : filterRowsStartingFrom(rows, startFrom, _exactPeriod)

  if (!rowsToUpdate.length) return

  const payloads: POSTCustomerPeriodRequest[] = rowsToUpdate.map((row: any) => {
    const { year, month } = parsePeriod(row.period)

    const basePayload: any = {
      ...(row.id ? { id: row.id } : {}),
      customer_segment_id: customerSegmentId,
      retention_rate: row.retention_rate ? Number(row.retention_rate) : null,
      total_customers: row.total_customers ? Math.round(Number(row.total_customers)) : null,
      new_customers: row.new_customers ? Math.round(Number(row.new_customers)) : null,
      year: year ?? 0,
      month: month ?? 0,
      config: row.config
    }

    const mappedValue = year !== undefined && month !== undefined ? periodValuesByKey?.[getPeriodMapKey(year, month)] : undefined
    const valueToApply = mappedValue ?? value
    const finalValue = customerSegmentRowKey === `retention_rate` ? valueToApply : Math.round(valueToApply)

    return {
      ...basePayload,
      [customerSegmentRowKey]: finalValue
    } as POSTCustomerPeriodRequest
  })

  await postCustomerSegmentPeriodBulk(financialModelId, payloads)
  queryClient.invalidateQueries({
    queryKey: CUSTOMER_SEGMENTS_DATA_KEY.concat([financialModelId, payload.queryPeriod ?? `forecast`, `monthly`])
  })
  resetGlobalQueryKey([TOTALS_KEY])
}

export default applySegmentValueToRow
